import { t } from "@lingui/macro";
import { BUDGET_PATH_SELECT_CHAKRA_STYLES } from "@src/components/ui-kit/budget-path-select/constants";
import type {
  BudgetPathSelectProps,
  BudgetPathTagOption,
} from "@src/components/ui-kit/budget-path-select/types";
import { toScopedValue } from "@src/components/ui-kit/budget-path-select/utils";
import type { IGroupOption } from "@src/components/ui-kit/Select";
import Select, { type SelectRef } from "@src/components/ui-kit/Select/Select";
import { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import {
  BudgetPathTagContainer,
  BudgetPathTagLabel,
  BudgetPathTagRemove,
  BudgetPathValueContainer,
} from "./components";

export const BudgetPathSelect = forwardRef<SelectRef, BudgetPathSelectProps>(
  function BudgetPathSelect(
    {
      selectedBrand,
      selectedProject,
      selectedBudgetItem,
      brandOptions,
      projectOptions,
      budgetItemOptions,
      brandError,
      projectError,
      budgetItemError,
      isDisabled,
      isLoadingBrands,
      isLoadingProjects,
      isLoadingBudgetItems,
      asPortal,
      menuMinWidth = "250px",
      onSelectBrand,
      onSelectProject,
      onSelectBudgetItem,
      onRemoveBrand,
      onRemoveProject,
      onRemoveBudgetItem,
      onInputChange,
    },
    forwardedRef,
  ) {
    const selectRef = useRef<SelectRef>(null);

    useImperativeHandle(forwardedRef, () => ({
      focus() {
        selectRef.current?.focus();
      },
      onMenuClose() {
        selectRef.current?.onMenuClose();
      },
      setValue(value, action, option) {
        selectRef.current?.setValue(value, action, option);
      },
    }));

    const selectedPath = [
      selectedBrand && {
        ...selectedBrand,
        value: toScopedValue("brand", selectedBrand.value),
      },
      selectedProject && {
        ...selectedProject,
        value: toScopedValue("project", selectedProject.value),
      },
      selectedBudgetItem && {
        ...selectedBudgetItem,
        value: toScopedValue("budget-item", selectedBudgetItem.value),
      },
    ].filter(Boolean) as BudgetPathTagOption[];
    const selectedValues = selectedPath.map(({ value }) => value);

    const currentStepOptions: IGroupOption[] = !selectedBrand
      ? [{ label: t`Brands`, options: brandOptions }]
      : !selectedProject
        ? [{ label: t`Projects`, options: projectOptions }]
        : budgetItemOptions;

    const options = [...currentStepOptions, ...selectedPath];

    const placeholder = !selectedBrand
      ? t`Select brand or paste budget item code`
      : !selectedProject
        ? t`Select project or paste budget item code`
        : t`Select budget item or paste budget item code`;

    const error = !selectedBrand
      ? brandError
      : !selectedProject
        ? projectError
        : budgetItemError;

    const isLoading = !selectedBrand
      ? isLoadingBrands
      : !selectedProject
        ? isLoadingProjects
        : isLoadingBudgetItems;

    const noOptionsMessage = () => {
      if (!selectedBrand) return t`No brands`;
      if (!selectedProject) return t`No projects`;
      return t`No budget items`;
    };

    const handleChange = (value?: string[]) => {
      const nextValues = Array.isArray(value) ? value : [];
      const currentStepValues = new Set(
        currentStepOptions.flatMap((group) =>
          group.options.map((option) => option.value),
        ),
      );

      if (nextValues.length > selectedValues.length) {
        const addedValue = nextValues.find((nextValue) =>
          currentStepValues.has(nextValue),
        );

        if (!addedValue) return;

        if (!selectedBrand) {
          onSelectBrand(addedValue);
          return;
        }

        if (!selectedProject) {
          onSelectProject(addedValue);
          return;
        }

        onSelectBudgetItem(addedValue);
        selectRef.current?.onMenuClose();
        return;
      }

      if (
        !!selectedBudgetItem &&
        !nextValues.includes(
          toScopedValue("budget-item", selectedBudgetItem.value),
        )
      ) {
        onRemoveBudgetItem();
        return;
      }

      if (
        !!selectedProject &&
        !nextValues.includes(toScopedValue("project", selectedProject.value))
      ) {
        onRemoveProject();
        return;
      }

      if (
        !!selectedBrand &&
        !nextValues.includes(toScopedValue("brand", selectedBrand.value))
      ) {
        onRemoveBrand();
      }
    };

    const components = useMemo(() => {
      return {
        MultiValueContainer: BudgetPathTagContainer,
        ValueContainer: BudgetPathValueContainer,
        MultiValueLabel: BudgetPathTagLabel,
        MultiValueRemove: BudgetPathTagRemove,
      };
    }, []);

    return (
      <Select
        ref={selectRef}
        isMulti
        hideSelectedOptions
        closeMenuOnSelect={false}
        components={components}
        placeholder={placeholder}
        menuMinWidth={menuMinWidth}
        options={options}
        value={selectedValues}
        error={error}
        isDisabled={isDisabled}
        isLoading={isLoading}
        asPortal={asPortal}
        chakraStyles={BUDGET_PATH_SELECT_CHAKRA_STYLES}
        noOptionsMessage={noOptionsMessage}
        onInputChange={onInputChange}
        onChange={handleChange}
      />
    );
  },
);
